import type { InventoryListItem } from '@/lib/api/schemas/inventory.schema'
import type { YmDriftState, YmEconomicsPrices } from '@/lib/api/schemas/ymarket-pricing.schema'
import { formatRubleAmount } from '@/lib/market-money'
import { unavailableReasonText } from '@/lib/warehouse/labels'
import { DRIFT_LABELS, YM_PRICE_LABELS } from '@/lib/ymarket-pricing'

export const YMARKET_DRIFT_MARK = '≠'
export const YMARKET_UNSET_MARK = '?'

const PRICE_DRIFT: ReadonlySet<YmDriftState> = new Set(['price', 'discount_base', 'minimum_for_promos'])

/** The compact mark shown where our and the Market's prices differ or ours is not set; none when in sync or unknown. */
export function ymarketDriftOf(prices: YmEconomicsPrices | null | undefined): { mark: string; label: string } | null {
  if (prices === null || prices === undefined) return null
  if (prices.driftState === 'forge_price_not_set') return { mark: YMARKET_UNSET_MARK, label: DRIFT_LABELS.forge_price_not_set }
  if (!PRICE_DRIFT.has(prices.driftState)) return null
  return { mark: YMARKET_DRIFT_MARK, label: DRIFT_LABELS[prices.driftState] }
}

function moneyOr(value: number | null, reason: string): string {
  return value === null ? `— (${unavailableReasonText(reason)})` : formatRubleAmount(value)
}

function marketSideReason(prices: YmEconomicsPrices, absent: string): string {
  if (prices.driftState === 'never_collected') return 'ymarket_prices_never_collected'
  if (prices.driftState === 'missing_on_ym') return 'ymarket_offer_missing'
  return absent
}

function minimumText(prices: YmEconomicsPrices): string {
  const ours = prices.forgeMinimumForPromos
  const market = prices.ymMinimumForPromos
  if (ours === null && market === null) return `— (${unavailableReasonText('ymarket_minimum_absent')})`
  const own = ours === null ? '—' : formatRubleAmount(ours)
  return ours === market ? own : `${own} (Маркет ${market === null ? '—' : formatRubleAmount(market)})`
}

function collectedText(collectedAt: string | null, stale: boolean): string {
  if (collectedAt === null) return `— (${unavailableReasonText('ymarket_prices_never_collected')})`
  const moment = new Date(collectedAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' })
  return stale ? `${moment}, устарели` : moment
}

/** Every Yandex price of a row, each under its own label; null when the row has no collected prices. */
export function ymarketPriceText(economics: InventoryListItem['ymarketEconomics']): string | null {
  const prices = economics?.prices ?? null
  if (economics === null || economics === undefined || prices === null) return null
  const drift = ymarketDriftOf(prices)
  const promo = economics.drivers.find((driver) => driver.kind === 'promo' && driver.active && driver.promoPrice !== null)
  return [
    `${YM_PRICE_LABELS.sellsFor}: ${moneyOr(economics.sellsFor, economics.sellsForReason ?? marketSideReason(prices, 'price_not_collected'))}`,
    `${YM_PRICE_LABELS.ourPrice}: ${moneyOr(prices.forgePrice, 'forge_price_not_set')}`,
    `${YM_PRICE_LABELS.marketPrice}: ${moneyOr(prices.ymPrice, marketSideReason(prices, 'price_not_collected'))}${drift === null ? '' : ` ${drift.mark} ${drift.label.toLocaleLowerCase('ru-RU')}`}`,
    ...(promo === undefined ? [] : [`${YM_PRICE_LABELS.promoPrice}: ${formatRubleAmount(promo.promoPrice)}`]),
    `Минимум для акций: ${minimumText(prices)}`,
    `Цены собраны: ${collectedText(prices.collectedAt, economics.stale)}`,
  ].join(' · ')
}

export const YMARKET_PRICES_UNAVAILABLE_TEXT = `Цены Маркета: — (${unavailableReasonText('ymarket_economics_unavailable')})`
